import {Injectable} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';

const CID_PATTERN = /^\d+\.\d+$/;

/**
 * Reads the GA4 client id handed to us by a reseller's embed snippet via the
 * `cid` query param.
 *
 * The `_ga` cookie on the reseller's marketing domain is unreadable from this
 * origin, and a client id read from *our* `_ga` would be a different identifier
 * that joins to nothing, so the query param is the only trustworthy source.
 */
@Injectable({providedIn: 'root'})
export class ResellerCidService {

  private _cid: string | null = null;

  constructor(private readonly _router: Router) {
    this._capture();

    this._router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this._capture());
  }

  /**
   * The cid is deliberately cached rather than re-read on demand.
   *
   * SignUpComponent.ngOnInit rewrites the URL to keep only utm_source,
   * utm_medium and invitation_data, so `cid` is gone from the address bar long
   * before the auth calls run. That rewrite goes through Location.replaceState,
   * which leaves the Router's own state untouched — but relying on that is
   * fragile, and replacing it with a router navigation would silently break
   * this feature. Caching the first valid value removes the dependency.
   *
   * Consequence worth knowing: a full page reload on /sign-up loses the cid
   * permanently, because the URL it reloads from no longer contains it.
   */
  public get(): string | null {
    return this._cid;
  }

  private _capture(): void {
    if (this._cid) {
      return;
    }

    const queryParams = this._router.routerState.snapshot.root.queryParams;
    const raw = queryParams && queryParams['cid'];

    if (typeof raw !== 'string') {
      return;
    }

    const cid = raw.trim();

    if (CID_PATTERN.test(cid)) {
      this._cid = cid;
    }
  }

}
